import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { PhoneIcon, CheckCircleIcon, ArrowLongRightIcon, ArrowLongLeftIcon } from '@heroicons/react/24/outline';
import { getServiceBySlug } from '../data/servicesData';
import ServiceContactForm from '../components/common/ServiceContactForm';
import Section from '../components/common/Section';
import './ServiceDetail.css';

const ServiceDetail = () => {
    const { slug } = useParams();
    const { i18n } = useTranslation();
    const isAr = i18n.language === 'ar';

    const service = getServiceBySlug(slug);
    if (!service) return <Navigate to="/services" replace />;

    const title = isAr ? service.titleAr : service.titleEn;
    const heroSubtitle = isAr ? service.heroSubtitleAr : service.heroSubtitleEn;
    const description = isAr ? service.descriptionAr : service.descriptionEn;
    const benefits = isAr ? service.benefitsAr : service.benefitsEn;
    const metaTitle = isAr ? service.metaTitleAr : service.metaTitleEn;
    const metaDesc = isAr ? service.metaDescAr : service.metaDescEn;
    const ArrowIcon = isAr ? ArrowLongLeftIcon : ArrowLongRightIcon;

    const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
    const itemVariants = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDesc} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDesc} />
                <link rel="canonical" href={`https://sigmaelevator-eg.com/services/${slug}`} />
                <html lang={i18n.language} dir={isAr ? 'rtl' : 'ltr'} />
            </Helmet>

            <main className="service-detail-page">
                {/* ── HERO ── */}
                <div
                    className="sd-hero"
                    style={{ backgroundImage: `url(${service.image})` }}
                    role="img"
                    aria-label={title}
                >
                    <div className="sd-hero__overlay" />
                    <div className="container sd-hero__content">
                        {/* Breadcrumb */}
                        <nav className="sd-breadcrumb" aria-label="breadcrumb">
                            <Link to="/">{isAr ? 'الرئيسية' : 'Home'}</Link>
                            <span className="sd-breadcrumb__sep">/</span>
                            <Link to="/services">{isAr ? 'الخدمات' : 'Services'}</Link>
                            <span className="sd-breadcrumb__sep">/</span>
                            <span>{title}</span>
                        </nav>

                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h1 className="sd-hero__title">{title}</h1>
                            <div className="accent-line" />
                            <p className="sd-hero__subtitle">{heroSubtitle}</p>

                            <div className="sd-hero__cta">
                                <a href="tel:+201020008449" className="sd-hero__phone">
                                    <PhoneIcon className="sd-hero__phone-icon" />
                                    <span>+201020008449</span>
                                </a>
                                <Link to="/contact" className="sd-hero__link">
                                    {isAr ? 'تواصل معنا' : 'Contact Us'}
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* ── DESCRIPTION ── */}
                <Section className="sd-desc-section">
                    <div className="sd-desc-grid">
                        <motion.div
                            className="sd-desc-text"
                            initial={{ opacity: 0, x: isAr ? 40 : -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                        >
                            <h2 className="sd-section-title">
                                {isAr ? `خدمة ${title}` : `About ${title}`}
                            </h2>
                            <div className="accent-line" />
                            <p className="sd-desc-body">{description}</p>

                            <Link to="/contact" className="sd-cta-link">
                                {isAr ? 'احصل على عرض سعر' : 'Get a Quote'}
                                <ArrowIcon className="sd-cta-link__icon" />
                            </Link>
                        </motion.div>

                        <motion.div
                            className="sd-desc-img"
                            initial={{ opacity: 0, x: isAr ? -40 : 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                        >
                            <img
                                src={service.image}
                                alt={`${title} - Sigma Elevators Egypt`}
                                className="sd-desc-img__img"
                            />
                        </motion.div>
                    </div>
                </Section>

                {/* ── BENEFITS ── */}
                <Section className="sd-benefits-section">
                    <div className="text-center sd-benefits-header">
                        <h2 className="sd-section-title">
                            {isAr ? 'المميزات والفوائد' : 'Key Features & Benefits'}
                        </h2>
                        <div className="accent-line center" />
                        <p className="sd-benefits-sub">
                            {isAr
                                ? 'لماذا تختار سيجما لهذه الخدمة'
                                : 'Why choose Sigma for this service'}
                        </p>
                    </div>

                    <motion.div
                        className="sd-benefits-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {benefits.map((b, i) => (
                            <motion.div key={i} className="sd-benefit-card" variants={itemVariants}>
                                <CheckCircleIcon className="sd-benefit-icon" />
                                <div>
                                    <h3 className="sd-benefit-title">{b.title}</h3>
                                    <p className="sd-benefit-desc">{b.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </Section>

                {/* ── CTA BANNER ── */}
                <Section className="sd-cta-banner">
                    <div className="sd-cta-banner__inner">
                        <h2>{isAr ? 'هل أنت مستعد للبدء؟' : 'Ready to Get Started?'}</h2>
                        <p>
                            {isAr
                                ? 'فريقنا جاهز لمساعدتك. تواصل معنا اليوم للحصول على استشارة مجانية.'
                                : "Our team is ready to help. Contact us today for a free consultation."}
                        </p>
                        <div className="sd-cta-banner__actions">
                            <a href="tel:+201020008449" className="btn btn-primary">
                                <PhoneIcon style={{ width: '1.1rem', marginInlineEnd: '0.4rem' }} />
                                +201020008449
                            </a>
                            <Link to="/contact" className="btn btn-outline">
                                {isAr ? 'نموذج التواصل' : 'Contact Form'}
                            </Link>
                        </div>
                    </div>
                </Section>

                {/* ── CONTACT FORM ── */}
                <Section className="sd-form-section">
                    <div className="sd-form-grid">
                        <div className="sd-form-intro">
                            <h2 className="sd-section-title">
                                {isAr ? 'تواصل معنا' : 'Get In Touch'}
                            </h2>
                            <div className="accent-line" />
                            <p className="sd-form-intro__body">
                                {isAr
                                    ? 'أرسل لنا استفسارك وسيرد عليك أحد خبرائنا في أقرب وقت ممكن.'
                                    : 'Send us your inquiry and one of our experts will get back to you as soon as possible.'}
                            </p>
                            <ul className="sd-form-contact-list">
                                <li>
                                    <PhoneIcon className="sd-form-contact-icon" />
                                    <div>
                                        <a href="tel:+201020008449">+201020008449</a>
                                        <a href="tel:+20226438448">+20226438448</a>
                                        <a href="tel:+201007707607">+201007707607</a>
                                    </div>
                                </li>
                                <li>
                                    <span className="sd-form-contact-icon" style={{ fontSize: '1.2rem' }}>✉</span>
                                    <a href="mailto:info@sigmaelevator-eg.com">info@sigmaelevator-eg.com</a>
                                </li>
                            </ul>
                        </div>

                        <ServiceContactForm serviceSlug={slug} />
                    </div>
                </Section>
            </main>
        </>
    );
};

export default ServiceDetail;
